import type { BaseEntity } from './BaseEntity';

/**
 * F 290 — THE USER RECORD. The kind the auth doctrine already assumed.
 *
 * WHY THIS FILE EXISTS. `pact/freshvibe-way-v8/47-auth-doctrine.md` §47.2, parked
 * on `acad433`, says: *"A user is an entity, like any other. It has an id. It has
 * an entity kind (`user`)."* The `EntityKind` union had no `user` in it, on main or
 * on the parked commit. The doctrine described a kind the codebase had never had.
 * This adds it. That is the whole job, and it is step 1 of FVC's sequence:
 * add the kind, then build the identity layer against it.
 *
 * THIS IS NOT THE IDENTITY LAYER. Nothing here authenticates anyone, verifies
 * anything, or produces a principal. `AuthAdapter` and `AuthRegistry` are parked
 * and untouched. This file is a record that can be stored, listed, referenced and
 * resolved — the substrate an identity layer would be written ON TOP of, not a
 * substitute for one.
 *
 * WHY IT IS NOT A NEAR-COPY OF App.ts. F 276 copied `Project.ts` because an app
 * belongs to exactly one project, and both facts — containment and scope — could
 * be carried on the record. **A user does not have that property. A user belongs
 * to many projects**, so there is no single `project: string` to require, and
 * copying F 276's required field would be a category error that silently
 * re-parented every user to whichever project touched it last. What DID carry
 * over unchanged is the mechanism F 275 proved: a record of kind K lives at
 * `K:<id>`, adapters store keys, and `QueryLayer.list(K)` finds them with no
 * adapter change. The pattern generalised for a THIRD time here, with zero
 * adapters touched.
 *
 * WHERE A USER SITS. A user is a ROOT, the same as a project. `parentId` is
 * inherited from `BaseEntity` and is left null. It is NOT a child of a project,
 * because containment is a tree and a user with many parents is not a tree.
 * `pact/freshvibe-way-v8/46-named-edges.md` §46.1 settles the vocabulary: *"A FCr
 * entity has exactly two relationships — containment (`parentId` + `order`) and
 * references (`fcid://` links)."* Membership is therefore a REFERENCE, and this
 * record carries it in `memberships`, naming projects by id.
 *
 * `edge` IS DELIBERATELY NOT USED. §46 is marked *"RULED — NOT YET IMPLEMENTED"*,
 * and what `edge` actually does on `BaseEntity` is scope sibling `order` —
 * `order` is only ever compared within a `(parentId, edge)` group. It is an
 * ordering device, not a relationship type. Using it for membership would be a
 * misuse of a field that means something else.
 */

/** Canonical fcid prefix for a user. As for every kind. */
export const USER_FCID_PREFIX = 'fcid://user/';

/** Stable fcid for a user id. The canonical reference. */
export function userFcid(id: string): string {
  return `${USER_FCID_PREFIX}${id}`;
}

/**
 * What a user may do in a project.
 *
 * BORROWED, NOT INVENTED. These three values are `PagePermission['role']` in
 * `src/types/models/Page.ts:41` — the role vocabulary FCr already uses for a
 * human on a thing. Reusing it here is deliberate: a second role vocabulary for
 * the same idea would be a third place to look and a second thing to migrate.
 */
export type UserRole = 'owner' | 'editor' | 'viewer';

/**
 * A user's membership of ONE project.
 *
 * `project` is a required string, matching `Conversation.project`, `Turn.project`
 * and `Agent.project` — the fleet's existing scoping field name.
 */
export interface UserMembership {
  project: string;
  role: UserRole;
}

/**
 * A user: a person, as an entity.
 */
export interface User extends BaseEntity {
  /** Canonical reference. Present on every kind; declared here as Project and App do. */
  fcid: string;
  /** Display name. The only field a human is required to supply. */
  name: string;
  /**
   * The user's email address, when they have one.
   *
   * OPTIONAL, and that is a decision worth naming. The doctrine never mentions an
   * email — §47.2 says only "it has an id" — so requiring one would be inventing a
   * constraint the design does not state. But FVC's `getEmailForReq` partitions
   * every request by exactly this value, and `admin@freshvibeapps.local` is a real
   * partition key in production, so the field has to exist before an identity
   * layer can be written against it. **Whether it should be REQUIRED is FVC's and
   * the operator's call, not this dispatch's** — F 290's own scope boundary says
   * "do not build the identity layer," and requiredness is a property of that
   * layer, not of the record.
   *
   * It is a plain string and is never parsed, never verified, and never a login.
   */
  email?: string;
  /** Human-facing label, when `name` is a slug and the display text differs. */
  label?: string;
  /** Free-form description. Never parsed. */
  description?: string;
  /**
   * The projects this user belongs to, and their role in each.
   *
   * A LIST, because a user may belong to many projects — this is the one place
   * the shape cannot copy App.ts, and the reason is the domain rather than the
   * mechanism. A user is not scoped by a single `project: string` because there
   * is no single one.
   *
   * Membership lives HERE, on the user, and the reverse direction is DERIVED from
   * it by `UserStore.listUsersOfProject`. One source of truth, read two ways. The
   * alternative — a `members` list on the project as well — would be two records
   * that have to be kept in agreement, and `App.ts` already records why pairing a
   * containment fact and a scope fact that happen to coincide is worth avoiding.
   */
  memberships?: UserMembership[];
}

/** True when `value` is a user record. Proves the kind survives a round-trip. */
export function isUser(value: unknown): value is User {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as User).fcid === 'string' &&
    (value as User).fcid.startsWith(USER_FCID_PREFIX) &&
    typeof (value as User).name === 'string'
  );
}

export interface CreateUserInput {
  id?: string;
  name: string;
  email?: string;
  label?: string;
  description?: string;
  memberships?: UserMembership[];
  order?: number | null;
  now?: string;
}

/**
 * Derive a stable id from a name. Same rule as `deriveAppId`.
 *
 * FROM THE NAME ONLY, and the first draft of this used the email's local part
 * when there was one. That is a bug, and it is worth recording: `createUser({name:
 * 'Ada'})` and `createUser({name: 'Ada', email: 'ada@x'})` produced 'ada' and
 * 'ada-lovelace' — two different users — so SUPPLYING AN EMAIL CHANGED THE
 * IDENTITY of an existing record. An id that moves when an optional field is
 * filled in is not an id; it is a race between two writes. `name` is required
 * and the email is not, so the name is the only stable basis.
 */
function deriveUserId(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/**
 * Construct a user.
 *
 * Fails loudly on an empty name. It does NOT require a project, and that is the
 * deliberate difference from `createApp`: a user with no memberships is a real
 * state — a person who has not joined anything yet — and refusing to construct
 * one would make the common case an error.
 */
export function createUser(input: CreateUserInput): User {
  const name = typeof input.name === 'string' ? input.name.trim() : '';
  if (!name) throw new Error('createUser: name is required and must be non-empty');
  const now = input.now ?? new Date().toISOString();
  const id = input.id?.trim() || deriveUserId(name);
  return {
    id,
    fcid: userFcid(id),
    name,
    email: input.email,
    label: input.label,
    description: input.description,
    memberships: input.memberships,
    // A user is a ROOT. Containment is a tree, and a person who belongs to many
    // projects has many parents — so there is no single parent to name here.
    parentId: null,
    order: input.order ?? null,
    createdAt: now,
    updatedAt: now,
  };
}
